// Story 8.5 — POST /api/v1/creators/me/payout/request.
//
// WHAT THIS ACTUALLY DOES, AND WHY THE COPY AROUND IT MATTERS: it does not pay
// anybody. It raises a SELF-BILLED INVOICE against the creator's earnings pot —
// the pot is debited, the invoice is stored and emailed, and Greenhold pays it
// at the settlement period's cut-off, in a batch.
//
// That is a supplier settlement, not a withdrawal, and the distinction is the
// one the whole money model rests on (docs/no-payout-pivot-2026-08-20.md,
// guardrail 2). So no string in this feature says "withdraw" or "cash out": the
// creator is a supplier being paid, and the UI has to read that way or the
// product and the paperwork describe two different things.
//
// Per-submit idempotency key (regenerated per submit, NOT per modal mount —
// the Stories 1.16/1.18/8.2 pattern): a network retry must not raise two
// invoices against one pot.

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { attachRequestIdToScope } from "../../lib/api-client";
import { PAYOUT_OPTIONS_QUERY_KEY, type PayoutRail } from "./usePayoutOptions";
import { SETTLEMENTS_QUERY_KEY } from "./useSettlements";
import type { Settlement } from "./useSettlements";

export type RequestPayoutError = {
  code: string;
  message: string;
  reason?: string;
  setup_url?: string;
  details?: { earnings_cents?: number; minimum_cents?: number };
};

export type RequestPayoutVariables = {
  amountCents: number;
  rail: PayoutRail;
  idempotencyKey: string;
};

async function postPayoutRequest(
  vars: RequestPayoutVariables,
): Promise<Settlement> {
  const response = await fetch("/api/v1/creators/me/payout/request", {
    method: "POST",
    credentials: "include",
    headers: {
      "content-type": "application/json",
      "x-idempotency-key": vars.idempotencyKey,
    },
    body: JSON.stringify({
      amount_cents: vars.amountCents,
      rail: vars.rail,
    }),
  });
  attachRequestIdToScope(response);
  if (!response.ok) {
    let detail: RequestPayoutError = {
      code: "PAYOUT_REQUEST_FAILED",
      message: `Could not request your payout (HTTP ${response.status})`,
    };
    try {
      const raw = await response.json();
      if (raw?.error) detail = { ...raw.error };
    } catch {
      // Body not JSON.
    }
    throw detail;
  }
  return (await response.json()) as Settlement;
}

export function useRequestPayout() {
  const queryClient = useQueryClient();
  return useMutation<Settlement, RequestPayoutError, RequestPayoutVariables>({
    mutationFn: postPayoutRequest,
    onSuccess: () => {
      // The earnings pot moved, a settlement appeared, and the wallet page's
      // two-pot display is now stale.
      queryClient.invalidateQueries({ queryKey: PAYOUT_OPTIONS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: SETTLEMENTS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: ["wallet-balance"] });
      queryClient.invalidateQueries({ queryKey: ["wallet-history"] });
    },
  });
}
